import React, { useState } from "react";
import { toast } from "sonner";
import { Send, MessageCircle, Mail, Phone, MapPin } from "lucide-react";
import { Reveal, Overline } from "@/components/Reveal";
import { useLang } from "@/context/LanguageContext";
import { CONTACT } from "@/data/content";

const SUBJECTS = ["WEDDING", "CORPORATE", "PRIVATE", "OTHER"];

export const Contact = () => {
  const { t } = useLang();
  const [form, setForm] = useState({ name: "", email: "", phone: "", event_date: "", subject: "WEDDING", message: "" });

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  // Fully static: compose the enquiry and open the visitor's email app (no backend).
  const buildEnquiry = () => {
    const subjectLabel = t.contact.subjects[form.subject];
    const lines = [
      `Name: ${form.name}`,
      `Email: ${form.email}`,
      form.phone ? `Phone: ${form.phone}` : null,
      `Event type: ${subjectLabel}`,
      form.event_date ? `Event date: ${form.event_date}` : null,
      "",
      form.message,
    ].filter((l) => l !== null).join("\n");
    return { subjectLabel, lines };
  };

  const submit = (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error(t.contact.validationError);
      return;
    }
    const { subjectLabel, lines } = buildEnquiry();
    const mailto = `mailto:${CONTACT.email}?subject=${encodeURIComponent(`Booking enquiry — ${subjectLabel}`)}&body=${encodeURIComponent(lines)}`;
    window.location.href = mailto;
    toast.success(t.contact.success);
  };

  const sendOnWhatsApp = () => {
    if (!form.name || !form.message) {
      // fall back to a generic message if the form isn't filled
      const generic = encodeURIComponent("Hello Olivier, I'd like to enquire about your magic for my event.");
      window.open(`https://wa.me/${CONTACT.whatsapp}?text=${generic}`, "_blank", "noopener");
      return;
    }
    const { lines } = buildEnquiry();
    window.open(`https://wa.me/${CONTACT.whatsapp}?text=${encodeURIComponent(lines)}`, "_blank", "noopener");
  };

  const inputClass =
    "w-full bg-transparent border-b border-[#1A1A1A]/20 py-3 text-[#1A1A1A] placeholder-[#7A7A7A] text-base font-light focus:outline-none focus:border-[#C5A880] transition-colors";

  return (
    <section id="contact" data-testid="contact-section" className="py-24 md:py-32 lg:py-40 bg-[#FAF9F6]">
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-14 lg:gap-20">
        <div className="lg:col-span-5">
          <Reveal><Overline className="mb-6">{t.contact.overline}</Overline></Reveal>
          <Reveal delay={0.1}>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight leading-[1.05] text-[#1A1A1A] mb-6">
              {t.contact.title}
            </h2>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="text-[#4A4A4A] text-base sm:text-lg font-light leading-relaxed mb-10 max-w-md">{t.contact.subtitle}</p>
          </Reveal>

          <Reveal delay={0.2}>
            <div className="space-y-5 mb-10">
              <a href={`mailto:${CONTACT.email}`} data-testid="contact-email-link" className="flex items-center gap-4 text-[#1A1A1A] hover:text-[#B0926A] transition-colors">
                <span className="w-11 h-11 flex items-center justify-center border border-[#1A1A1A]/15"><Mail size={18} /></span>
                <span className="text-sm font-light">{CONTACT.email}</span>
              </a>
              <a href={`tel:${CONTACT.phone.replace(/\s/g, "")}`} data-testid="contact-phone-link" className="flex items-center gap-4 text-[#1A1A1A] hover:text-[#B0926A] transition-colors">
                <span className="w-11 h-11 flex items-center justify-center border border-[#1A1A1A]/15"><Phone size={18} /></span>
                <span className="text-sm font-light">{CONTACT.phone}</span>
              </a>
              <div className="flex items-center gap-4 text-[#1A1A1A]">
                <span className="w-11 h-11 flex items-center justify-center border border-[#1A1A1A]/15"><MapPin size={18} /></span>
                <span className="text-sm font-light">{CONTACT.location}</span>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.25}>
            <button
              type="button"
              data-testid="whatsapp-btn"
              onClick={sendOnWhatsApp}
              className="inline-flex items-center gap-3 px-8 py-4 bg-[#25D366] text-white text-xs font-medium tracking-[0.2em] uppercase hover:bg-[#128C7E] transition-colors duration-300"
            >
              <MessageCircle size={18} /> {t.contact.whatsapp}
            </button>
          </Reveal>
        </div>

        <div className="lg:col-span-7">
          <Reveal delay={0.15}>
            <form data-testid="contact-form" onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-7">
              <div className="sm:col-span-1">
                <input data-testid="contact-name" className={inputClass} placeholder={t.contact.name} value={form.name} onChange={update("name")} required />
              </div>
              <div className="sm:col-span-1">
                <input data-testid="contact-email" type="email" className={inputClass} placeholder={t.contact.email} value={form.email} onChange={update("email")} required />
              </div>
              <div className="sm:col-span-1">
                <input data-testid="contact-phone" className={inputClass} placeholder={t.contact.phone} value={form.phone} onChange={update("phone")} />
              </div>
              <div className="sm:col-span-1">
                <input data-testid="contact-date" className={inputClass} placeholder={t.contact.date} value={form.event_date} onChange={update("event_date")} />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-[11px] tracking-[0.2em] uppercase text-[#7A7A7A] mb-3">{t.contact.subjectLabel}</label>
                <div className="flex flex-wrap gap-3">
                  {SUBJECTS.map((s) => (
                    <button
                      key={s}
                      type="button"
                      data-testid={`subject-${s}`}
                      onClick={() => setForm((f) => ({ ...f, subject: s }))}
                      className={`px-5 py-2.5 text-[11px] tracking-[0.15em] uppercase border transition-colors ${
                        form.subject === s ? "bg-[#1A1A1A] text-[#FAF9F6] border-[#1A1A1A]" : "border-[#1A1A1A]/20 text-[#1A1A1A]/70 hover:border-[#C5A880]"
                      }`}
                    >
                      {t.contact.subjects[s]}
                    </button>
                  ))}
                </div>
              </div>
              <div className="sm:col-span-2">
                <textarea data-testid="contact-message" rows={4} className={`${inputClass} resize-none`} placeholder={t.contact.message} value={form.message} onChange={update("message")} required />
              </div>
              <div className="sm:col-span-2">
                <button
                  data-testid="contact-submit"
                  type="submit"
                  className="inline-flex items-center gap-3 px-10 py-4 bg-[#1A1A1A] text-[#FAF9F6] text-xs font-medium tracking-[0.2em] uppercase hover:bg-[#C5A880] transition-colors duration-300"
                >
                  {t.contact.submit}
                  <Send size={16} />
                </button>
              </div>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
